import React, { useState } from 'react';
import ClientSearchSelect from './ClientSearchSelect';

const StepClientDetails = ({ formData, updateFormData, nextStep, disabled, onSelectClient, onSaveAsClient, clientId }) => {
  const data = formData.clientDetails;
  const [savingClient, setSavingClient] = useState(false);

  const handleChange = (field, value) => {
    updateFormData('clientDetails', { [field]: value });
  };

  const handleSaveAsClient = async () => {
    if (!data.fullName?.trim()) return;
    setSavingClient(true);
    try {
      await onSaveAsClient();
    } finally {
      setSavingClient(false);
    }
  };

  return (
    <fieldset disabled={disabled}>
      <h4 className="soa-step-title">Client Details</h4>
      <p className="soa-step-subtitle">Enter the client's personal and contact information</p>

      {/* Client search */}
      <ClientSearchSelect onSelectClient={onSelectClient} disabled={disabled} />

      <div className="row">
        <div className="col-md-6 soa-form-group">
          <label>Full Name *</label>
          <input type="text" className="form-control" value={data.fullName} onChange={(e) => handleChange('fullName', e.target.value)} placeholder="e.g. John Smith" />
        </div>
        <div className="col-md-6 soa-form-group">
          <label>Date of Birth</label>
          <input type="date" className="form-control" value={data.dateOfBirth} onChange={(e) => handleChange('dateOfBirth', e.target.value)} />
        </div>
      </div>

      <div className="soa-form-group">
        <label>Address</label>
        <input type="text" className="form-control" value={data.address} onChange={(e) => handleChange('address', e.target.value)} placeholder="Street address, suburb, state, postcode" />
      </div>

      <div className="row">
        <div className="col-md-6 soa-form-group">
          <label>Email</label>
          <input type="email" className="form-control" value={data.email} onChange={(e) => handleChange('email', e.target.value)} />
        </div>
        <div className="col-md-6 soa-form-group">
          <label>Phone</label>
          <input type="tel" className="form-control" value={data.phone} onChange={(e) => handleChange('phone', e.target.value)} />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 soa-form-group">
          <label>Employment Status</label>
          <select className="form-select" value={data.employment} onChange={(e) => handleChange('employment', e.target.value)}>
            <option value="">Select...</option>
            <option value="Employed - Full Time">Employed — Full Time</option>
            <option value="Employed - Part Time">Employed — Part Time</option>
            <option value="Self-Employed">Self-Employed</option>
            <option value="Retired">Retired</option>
            <option value="Not Employed">Not Employed</option>
          </select>
        </div>
        <div className="col-md-6 soa-form-group">
          <label>Employer</label>
          <input type="text" className="form-control" value={data.employer} onChange={(e) => handleChange('employer', e.target.value)} />
        </div>
      </div>

      {/* Homeowner */}
      <div className="soa-form-group">
        <label>Homeowner</label>
        <select className="form-select" value={data.homeowner || 'Yes'} onChange={(e) => handleChange('homeowner', e.target.value)}>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        <small className="text-muted">Owns principal place of residence — affects age pension asset test thresholds</small>
      </div>

      {/* Couple toggle */}
      <div className="soa-section-banner">
        <i className="bi bi-people"></i>
        Couple / Joint Advice
      </div>

      <div className="soa-form-group">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="isCouple"
            checked={data.isCouple}
            onChange={(e) => handleChange('isCouple', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="isCouple">This SOA is for a couple</label>
        </div>
      </div>

      {data.isCouple && (
        <div className="row">
          <div className="col-md-6 soa-form-group">
            <label>Partner Full Name</label>
            <input type="text" className="form-control" value={data.partnerName} onChange={(e) => handleChange('partnerName', e.target.value)} />
          </div>
          <div className="col-md-3 soa-form-group">
            <label>Partner Date of Birth</label>
            <input type="date" className="form-control" value={data.partnerDob} onChange={(e) => handleChange('partnerDob', e.target.value)} />
          </div>
          <div className="col-md-3 soa-form-group">
            <label>Partner Employment</label>
            <select className="form-select" value={data.partnerEmployment} onChange={(e) => handleChange('partnerEmployment', e.target.value)}>
              <option value="">Select...</option>
              <option value="Employed - Full Time">Employed — Full Time</option>
              <option value="Employed - Part Time">Employed — Part Time</option>
              <option value="Self-Employed">Self-Employed</option>
              <option value="Retired">Retired</option>
              <option value="Not Employed">Not Employed</option>
            </select>
          </div>
        </div>
      )}

      {/* Dependants */}
      <div className="soa-section-banner">
        <i className="bi bi-person-hearts"></i>
        Dependants
      </div>

      <div className="soa-form-group">
        <label>Dependant Details</label>
        <textarea
          className="form-control"
          value={data.dependants}
          onChange={(e) => handleChange('dependants', e.target.value)}
          placeholder="e.g. 2 children — Sarah (12) and Tom (9)"
          rows={2}
        />
      </div>

      {/* Save as Client */}
      {data.fullName?.trim() && (
        <div className="d-flex align-items-center gap-2 mt-3 mb-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleSaveAsClient}
            disabled={savingClient}
          >
            {savingClient ? (
              <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
            ) : clientId ? (
              <><i className="bi bi-arrow-repeat me-1"></i>Update Client Record</>
            ) : (
              <><i className="bi bi-person-plus me-1"></i>Save as Client</>
            )}
          </button>
          {clientId && (
            <small className="text-muted">
              <i className="bi bi-link-45deg me-1"></i>Linked to client record
            </small>
          )}
        </div>
      )}

      <div className="soa-step-nav">
        <div></div>
        <button className="btn btn-primary" onClick={nextStep}>
          Next: Scope of Advice <i className="bi bi-arrow-right ms-1"></i>
        </button>
      </div>
    </fieldset>
  );
};

export default StepClientDetails;
